/**
 * CV preset save/load utility.
 *
 * Stores CV configuration presets in localStorage so recruiters can save
 * their preferred CV setup (theme, section order, hidden items, etc.) and
 * reapply it across different workers.
 *
 * Mirrors the pattern of `WorkerFilterPresets` (system + user presets).
 */

import { PromptConfig } from '../components/Worker/CVPromptConfigPanel';

const STORAGE_KEY = 'optima:cv-presets';

export interface CVPreset {
    id: string;
    name: string;
    isSystem?: boolean;
    /** Configure-step settings (language, theme, AI instructions, contact info, etc.) */
    config: PromptConfig;
    /** Preview-step settings */
    sectionOrder: string[];           // empty = template default
    hiddenSectionIds: string[];       // hidden section data-testids
    hiddenItemIds: string[];          // hidden item data-testids
    includeQuestionnairePage: boolean;
    createdAt?: number;
}

const SYSTEM_PRESETS: CVPreset[] = [
    {
        id: 'system:standard',
        name: 'Standard',
        isSystem: true,
        config: {
            categoryToggles: { personal: true, professional: true, other: true, professionContext: true },
            questionOverrides: [],
            questionToggles: {},
            extraInstructions: '',
            language: 'NL',
            contactInfo: { name: '', email: '', phone: '' },
            theme: 'modern-green',
            includeQuestionnairePage: true,
        },
        sectionOrder: [],
        hiddenSectionIds: [],
        hiddenItemIds: [],
        includeQuestionnairePage: true,
    },
    {
        id: 'system:highlights-first',
        name: 'Highlights First',
        isSystem: true,
        config: {
            categoryToggles: { personal: true, professional: true, other: true, professionContext: true },
            questionOverrides: [],
            questionToggles: {},
            extraInstructions: 'Lead with the strongest professional highlights.',
            language: 'NL',
            contactInfo: { name: '', email: '', phone: '' },
            theme: 'modern-green',
            includeQuestionnairePage: true,
        },
        sectionOrder: [
            'profile-section',
            'highlights-section',
            'introduction-section',
            'work-experience-section',
            'education-section',
            'languages-section',
        ],
        hiddenSectionIds: [],
        hiddenItemIds: [],
        includeQuestionnairePage: true,
    },
    {
        id: 'system:minimal',
        name: 'Minimal',
        isSystem: true,
        config: {
            categoryToggles: { personal: true, professional: true, other: false, professionContext: false },
            questionOverrides: [],
            questionToggles: {},
            extraInstructions: 'Keep concise. Emphasize core experience only.',
            language: 'NL',
            contactInfo: { name: '', email: '', phone: '' },
            theme: 'minimal-gray',
            includeQuestionnairePage: false,
        },
        sectionOrder: [],
        hiddenSectionIds: [],
        hiddenItemIds: [],
        includeQuestionnairePage: false,
    },
];

function loadUserPresets(): CVPreset[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveUserPresets(presets: CVPreset[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch (e) {
        console.error('Failed to save CV presets:', e);
    }
}

export function listPresets(): CVPreset[] {
    return [...SYSTEM_PRESETS, ...loadUserPresets()];
}

export function getPreset(id: string): CVPreset | undefined {
    return listPresets().find(p => p.id === id);
}

export function savePreset(preset: Omit<CVPreset, 'id' | 'isSystem' | 'createdAt'>): CVPreset {
    const userPresets = loadUserPresets();
    const newPreset: CVPreset = {
        ...preset,
        id: `user:${Date.now()}`,
        createdAt: Date.now(),
    };
    saveUserPresets([...userPresets, newPreset]);
    return newPreset;
}

export function updatePreset(id: string, updates: Omit<CVPreset, 'id' | 'isSystem' | 'createdAt' | 'name'>): CVPreset | undefined {
    if (id.startsWith('system:')) return undefined; // system presets are read-only
    const userPresets = loadUserPresets();
    const idx = userPresets.findIndex(p => p.id === id);
    if (idx === -1) return undefined;
    const updated: CVPreset = { ...userPresets[idx], ...updates };
    userPresets[idx] = updated;
    saveUserPresets(userPresets);
    return updated;
}

export function deletePreset(id: string): void {
    if (id.startsWith('system:')) return; // system presets can't be deleted
    const userPresets = loadUserPresets().filter(p => p.id !== id);
    saveUserPresets(userPresets);
}

/**
 * Check whether a preset name is already in use (case-insensitive).
 * Excludes the preset with `excludeId` from the check (useful when renaming).
 */
export function isPresetNameTaken(name: string, excludeId?: string): boolean {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return listPresets().some(
        p => p.id !== excludeId && p.name.trim().toLowerCase() === trimmed,
    );
}

/**
 * Deep-canonicalize a preset payload so we can stringify-compare two states
 * meaningfully (toggling X on then off won't produce a false "dirty" signal).
 *
 * - Sorts object keys at all levels
 * - Strips `questionToggles` entries whose value is `true` (true is the default)
 * - Sorts arrays of strings (e.g. hiddenSectionIds)
 * - Strips empty contactInfo fields
 */
function canonicalize(value: any): any {
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) {
        // For string arrays, sort. For object arrays (e.g. questionOverrides), sort by id key if available.
        if (value.every(v => typeof v === 'string')) {
            return [...value].sort();
        }
        return value.map(canonicalize);
    }
    if (typeof value === 'object') {
        const sorted: Record<string, any> = {};
        Object.keys(value).sort().forEach(k => {
            sorted[k] = canonicalize(value[k]);
        });
        return sorted;
    }
    return value;
}

export function canonicalizePresetPayload(payload: {
    config: any;
    sectionOrder: string[];
    hiddenSectionIds: string[];
    hiddenItemIds: string[];
    includeQuestionnairePage: boolean;
}): string {
    const config = payload.config ? { ...payload.config } : null;

    if (config) {
        // Strip questionToggles entries where value is `true` (default)
        if (config.questionToggles && typeof config.questionToggles === 'object') {
            const filtered: Record<string, boolean> = {};
            Object.entries(config.questionToggles).forEach(([k, v]) => {
                if (v === false) filtered[k] = false;
            });
            config.questionToggles = filtered;
        }
        // Sort questionOverrides by id for stable comparison
        if (Array.isArray(config.questionOverrides)) {
            config.questionOverrides = [...config.questionOverrides].sort((a, b) => a.id - b.id);
        }
        // Treat empty contactInfo fields as equivalent (some flows use '' vs undefined)
        if (config.contactInfo) {
            config.contactInfo = {
                name: (config.contactInfo.name || '').trim(),
                email: (config.contactInfo.email || '').trim(),
                phone: (config.contactInfo.phone || '').trim(),
            };
        }
    }

    return JSON.stringify(canonicalize({
        config,
        sectionOrder: payload.sectionOrder,
        hiddenSectionIds: payload.hiddenSectionIds,
        hiddenItemIds: payload.hiddenItemIds,
        includeQuestionnairePage: payload.includeQuestionnairePage,
    }));
}

export function isSystemPreset(preset: CVPreset): boolean {
    return preset.isSystem === true || preset.id.startsWith('system:');
}
